import React from "react";

import ThemeContext from "~/common/ThemeContext";

import type { SmileChoices } from "../ResponseForm/validation";
import { ReactComponent as NeutralDefaultDark } from "./css/faces-icons/default/dark/neutral-dark.svg";
import { ReactComponent as SatisfiedDefaultDark } from "./css/faces-icons/default/dark/satisfied-dark.svg";
import { ReactComponent as UnsatisfiedDefaultDark } from "./css/faces-icons/default/dark/unsatisfied-dark.svg";
import { ReactComponent as VerySatisfiedDefaultDark } from "./css/faces-icons/default/dark/very-satisfied-dark.svg";
import { ReactComponent as VeryUnsatisfiedDefaultDark } from "./css/faces-icons/default/dark/very-unsatisfied-dark.svg";
import { ReactComponent as NeutralDefault } from "./css/faces-icons/default/neutral.svg";
import { ReactComponent as SatisfiedDefault } from "./css/faces-icons/default/satisfied.svg";
import { ReactComponent as UnsatisfiedDefault } from "./css/faces-icons/default/unsatisfied.svg";
import { ReactComponent as VerySatisfiedDefault } from "./css/faces-icons/default/very-satisfied.svg";
import { ReactComponent as VeryUnsatisfiedDefault } from "./css/faces-icons/default/very-unsatisfied.svg";
import { ReactComponent as NeutralOn } from "./css/faces-icons/on/neutral-on.svg";
import { ReactComponent as SatisfiedOn } from "./css/faces-icons/on/satisfied-on.svg";
import { ReactComponent as UnsatisfiedOn } from "./css/faces-icons/on/unsatisfied-on.svg";
import { ReactComponent as VerySatisfiedOn } from "./css/faces-icons/on/very-satisfied-on.svg";
import { ReactComponent as VeryUnsatisfiedOn } from "./css/faces-icons/on/very-unsatisfied-on.svg";
import animationData from "./css/feedback-respond-animation-test.json";
import RangeInput, { type RangeInputConsumerProps, type RangeInputItemProps } from "./RangeInput";

import styles from "./css/FaceInput.module.css";

const FACE_VALUES = ["none", "very satisfied", "satisfied", "neutral", "unsatisfied", "very unsatisfied"] as const;
export type FaceValue = (typeof FACE_VALUES)[number];

const valueToChoiceMap: { [k in Exclude<FaceValue, "none">]: SmileChoices[number] } = {
	"very unsatisfied": { label: "Very Unsatisfied", value: 1 },
	unsatisfied: { label: "Unsatisfied", value: 2 },
	neutral: { label: "Neutral", value: 3 },
	satisfied: { label: "Satisfied", value: 4 },
	"very satisfied": { label: "Very Satisfied", value: 5 },
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function isFaceValue(value: any): value is FaceValue {
	return FACE_VALUES.includes(value);
}

const FACES: { [k in FaceValue]: [typeof NeutralDefault, typeof NeutralOn] } = {
	none: [NeutralDefault, NeutralOn],
	"very satisfied": [VerySatisfiedDefault, VerySatisfiedOn],
	satisfied: [SatisfiedDefault, SatisfiedOn],
	neutral: [NeutralDefault, NeutralOn],
	unsatisfied: [UnsatisfiedDefault, UnsatisfiedOn],
	"very unsatisfied": [VeryUnsatisfiedDefault, VeryUnsatisfiedOn],
};

const FACES_DARK: { [k in FaceValue]: [typeof NeutralDefault, typeof NeutralOn] } = {
	none: [NeutralDefaultDark, NeutralOn],
	"very satisfied": [VerySatisfiedDefaultDark, VerySatisfiedOn],
	satisfied: [SatisfiedDefaultDark, SatisfiedOn],
	neutral: [NeutralDefaultDark, NeutralOn],
	unsatisfied: [UnsatisfiedDefaultDark, UnsatisfiedOn],
	"very unsatisfied": [VeryUnsatisfiedDefaultDark, VeryUnsatisfiedOn],
};

interface BaseFaceProps {
	selected?: boolean;
	type: FaceValue;
}

type FaceProps = BaseFaceProps & Omit<React.SVGProps<SVGSVGElement>, keyof BaseFaceProps>;

export function Face({ selected, type, ...svgProps }: FaceProps) {
	const { isDarkMode } = React.useContext(ThemeContext);
	const Component = isDarkMode ? FACES_DARK[type][+!!selected] : FACES[type][+!!selected];
	return <Component {...svgProps} />;
}

type FaceWrapperProps = RangeInputItemProps<FaceValue>;

function FaceWrapper({ selected, value, hoveredValue }: FaceWrapperProps) {
	return <Face className={styles.face} type={value} selected={selected || hoveredValue === value} />;
}

export default function FaceInput(props: RangeInputConsumerProps<FaceValue>) {
	return (
		<RangeInput<FaceValue>
			itemComponent={FaceWrapper}
			animationOptions={{
				animationData,
				rendererSettings: {
					className: styles.lottie,
				},
			}}
			options={["very unsatisfied", "unsatisfied", "neutral", "satisfied", "very satisfied"]}
			lottieProps={{
				width: "3em",
				height: "3em",
				// @ts-ignore
				style: { overflow: "visible !important", position: "absolute", transform: "translate(-20%, -10%)" },
			}}
			{...props}
		/>
	);
}

FaceInput.valueToChoiceMap = valueToChoiceMap;
